/**
 * EIS-1124 PART A -- pure helpers behind the rep-facing "Possible duplicates"
 * panel. No React, no network: see helpers.test.ts.
 */
import { Group, Member, Suggestion } from './types';

export type PillVariant = 'direct' | 'distributor' | 'neutral';

/**
 * Which pill style a data_source_label gets. Matched case-insensitively so a
 * differently-cased raw string from the backend still gets styled rather
 * than silently falling through to neutral. Anything that isn't recognized
 * as Direct/Distributor -- Badger explicitly included -- gets the neutral
 * pill (channel-pill classes reused from MentionComposer.module.scss; see
 * PossibleDuplicates.module.scss).
 */
export const pillVariantForLabel = (label?: string | null): PillVariant => {
  const normalized = (label || '').trim().toLowerCase();
  if (normalized === 'direct') return 'direct';
  if (normalized === 'distributor') return 'distributor';
  return 'neutral';
};

export const memberDisplayName = (member: Pick<Member, 'id' | 'display_name'>): string =>
  member.display_name || `Account #${member.id}`;

const cityState = (member: Pick<Member, 'city' | 'state'>): string =>
  [member.city, member.state].filter(Boolean).join(', ');

export interface CandidateRow {
  memberId: number;
  displayName: string;
  pillLabel: string;
  pillVariant: PillVariant;
  cityState: string;
  scorePercent: string;
  tier: string;
}

/**
 * One row per OTHER member of a suggestion (the current practitioner is
 * excluded even though the contract's `members` includes them). Also used
 * for rendering a Group's members (active_group / my_pending) by passing
 * `suggested_confidence`/`suggested_tier` as undefined -- those rows then
 * carry an empty score/tier rather than "0%", since there is nothing to
 * score there.
 */
export const buildCandidateRows = (
  suggestion: Pick<Suggestion, 'members'> & Partial<Pick<Suggestion, 'suggested_confidence' | 'suggested_tier'>>,
  currentPractitionerId: number,
): CandidateRow[] => {
  const hasScore = typeof suggestion.suggested_confidence === 'number';
  const scorePercent = hasScore ? `${Math.round((suggestion.suggested_confidence as number) * 100)}%` : '';
  const tier = suggestion.suggested_tier || '';

  return (suggestion.members || [])
    .filter((member) => member.id !== currentPractitionerId)
    .map((member) => ({
      memberId: member.id,
      displayName: memberDisplayName(member),
      pillLabel: member.data_source_label || member.data_source || 'Unknown',
      pillVariant: pillVariantForLabel(member.data_source_label),
      cityState: cityState(member),
      scorePercent,
      tier,
    }));
};

export type ChipKind = 'verified' | 'pending' | 'suggestions';

export interface ChipState {
  label: string;
  kind: ChipKind;
}

const VERIFIED_OR_BEYOND: Group['status'][] = ['verified', 'ready_to_send', 'synced', 'sync_failed'];

/**
 * The single summary chip for this practitioner's match state, in priority
 * order: an already-verified (or further along) group beats a group this rep
 * proposed and is still waiting on, which beats unreviewed suggestions.
 * `active_group` can itself carry status 'proposed' (the contract's
 * visibility rule: a rep sees their OWN proposed group as active_group) --
 * treated the same as `my_pending` defensively, in case the two ever
 * disagree.
 */
export const chipForState = ({
  active_group,
  my_pending,
  suggestions,
}: {
  active_group: Group | null;
  my_pending: Group[];
  suggestions: Suggestion[];
}): ChipState | null => {
  if (active_group && VERIFIED_OR_BEYOND.includes(active_group.status)) {
    return { label: 'Verified', kind: 'verified' };
  }
  if ((my_pending && my_pending.length > 0) || (active_group && active_group.status === 'proposed')) {
    return { label: 'Linked, pending review', kind: 'pending' };
  }
  if (suggestions && suggestions.length > 0) {
    const n = suggestions.length;
    return { label: `${n} possible duplicate${n === 1 ? '' : 's'}`, kind: 'suggestions' };
  }
  return null;
};
